import InfoIcon from '@mui/icons-material/Info';

import React from 'react';

import {
  Company,
  isServiceTitanApiCompany,
  isServiceTitanPortalV2Company,
  isStCapacityCompany,
  isSwapiV1Company,
  isSwapiV2Company,
  SupportedProviderEnum
} from '_lib/api/apiTypes.shim';
import { Link } from '_lib/components/Link/Link';
import { Stack } from '_lib/components/Stack/Stack';
import { Tooltip } from '_lib/components/Tooltip/Tooltip';

import { Typography } from '_lib/components/Typography/Typography';
import { useMediaQuery } from '_lib/hooks/useMediaQuery';

import { KeyValueGrid } from '../../../../KeyValueGrid/KeyValueGrid';

const displayProviderType = (providerType: SupportedProviderEnum): string => {
  const providerTypeMap: Record<SupportedProviderEnum, string> = {
    POOL_BRAIN: 'Pool Brain',
    SERVICE_TITAN: 'Service Titan',
    SERVICE_TITAN_API: 'Service Titan API',
    ST_CAPACITY: 'ST Capacity',
    SWAPI: 'SWAPI',
    POINTMAN: 'Pointman'
  };
  return providerTypeMap[providerType] || providerType;
};

const hasClientIdAndSecret = (dataProvider: NonNullable<Company['dataProvider']>) => {
  return (
    isServiceTitanApiCompany({ dataProvider }) ||
    isStCapacityCompany({ dataProvider }) ||
    isServiceTitanPortalV2Company({ dataProvider })
  );
};

type CompanyConfigDataProviderDisplayProps = {
  dataProvider: NonNullable<Company['dataProvider']>;
};

export const CompanyConfigDataProviderDisplay = (props: CompanyConfigDataProviderDisplayProps) => {
  const { dataProvider } = props;
  const singleColumn = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  const gridItemSize = singleColumn ? 12 : 6;

  const providerOptions = (dataProvider.providerOptions || {}) as Record<string, unknown>;

  const keyValueData = [];

  keyValueData.push({
    label: 'Provider',
    textOrComponent: displayProviderType(dataProvider.providerType)
  });

  if (dataProvider.providerVersion) {
    keyValueData.push({
      label: 'Provider Version',
      textOrComponent: dataProvider.providerVersion
    });
  }

  // (multi providerType) - tenantId
  if (providerOptions.tenantId) {
    keyValueData.push({
      label: 'Tenant ID',
      textOrComponent: String(providerOptions.tenantId)
    });
  }

  // ST V2 useAdaptiveCapacity
  if (isServiceTitanPortalV2Company({ dataProvider }) && 'useAdaptiveCapacity' in providerOptions) {
    keyValueData.push({
      label: 'Use Adaptive Capacity',
      textOrComponent: providerOptions.useAdaptiveCapacity ? 'Yes' : 'No'
    });
  }

  // ST V2 isEnterprise
  if (isServiceTitanPortalV2Company({ dataProvider }) && 'isEnterprise' in providerOptions) {
    keyValueData.push({
      label: 'Is Enterprise',
      textOrComponent: providerOptions.isEnterprise ? 'Yes' : 'No'
    });
  }

  // (multi providerType) - portalName
  if (providerOptions.portalName && !providerOptions.isEnterprise) {
    keyValueData.push({
      label: 'Portal Name',
      textOrComponent: String(providerOptions.portalName)
    });
  }

  // (multi providerType) - businessUnitName
  if (providerOptions.businessUnitName) {
    keyValueData.push({
      label: 'Business Unit',
      textOrComponent: String(providerOptions.businessUnitName)
    });
  }

  // (multi providerType) - defaultCampaignName
  if (providerOptions.defaultCampaignName) {
    keyValueData.push({
      label: 'Default Campaign',
      textOrComponent: String(providerOptions.defaultCampaignName)
    });
  }

  // (multi providerType) - outboundCampaignName
  if (providerOptions.outboundCampaignName) {
    keyValueData.push({
      label: 'Outbound Campaign',
      textOrComponent: String(providerOptions.outboundCampaignName)
    });
  }

  // skillBasedAvailability
  if ('skillBasedAvailability' in providerOptions) {
    keyValueData.push({
      label: 'Skill Based Availability',
      textOrComponent: (
        <Stack direction='row' spacing={1} useFlexGap sx={{ alignItems: 'center' }}>
          <Typography text={providerOptions.skillBasedAvailability ? 'Yes' : 'No'} />
          <Tooltip title='Whether or not the "Skills" capacity enhancement is enabled for the company. To verify go to Service Titan > Company Settings > Capacity Planning > Configuration.'>
            <InfoIcon fontSize='small' color='primary' />
          </Tooltip>
        </Stack>
      )
    });
  }

  // (multi providerType) - Client ID (masked)
  if (hasClientIdAndSecret(dataProvider)) {
    keyValueData.push({
      label: 'Client ID',
      textOrComponent: '**********'
    });
  }

  // (multi providerType) - Client Secret (masked)
  if (hasClientIdAndSecret(dataProvider)) {
    keyValueData.push({
      label: 'Client Secret',
      textOrComponent: '**********'
    });
  }

  // Pool Brain - Company ID
  if (providerOptions.companyId) {
    keyValueData.push({
      label: 'Company ID',
      textOrComponent: String(providerOptions.companyId)
    });
  }

  // Pool Brain - Customer Search URL
  if (providerOptions.customerSearchUrl) {
    keyValueData.push({
      label: 'Customer Search URL',
      textOrComponent: (
        <Tooltip title='This URL will be used to take agents to the Customer Search view for the Pool Brain Company when a customer cannot be found. Testing the link is a good way to confirm that the correct company has been connected.'>
          <Link href={String(providerOptions.customerSearchUrl)} target='_blank' rel='noopener noreferrer'>
            View Customers
          </Link>
        </Tooltip>
      )
    });
  }

  // SWAPI - swapiUrl
  if (providerOptions.swapiUrl) {
    keyValueData.push({
      label: 'SWAPI URL',
      textOrComponent: String(providerOptions.swapiUrl)
    });
  }

  // SWAPI - CallReason
  if (providerOptions.CallReason) {
    keyValueData.push({
      label: 'Call Reason',
      textOrComponent: String(providerOptions.CallReason)
    });
  }

  // SWAPI - CompanyNo
  if (providerOptions.CompanyNo) {
    keyValueData.push({
      label: 'Company No',
      textOrComponent: String(providerOptions.CompanyNo)
    });
  }

  // SWAPI - MasterID
  if (providerOptions.MasterID) {
    keyValueData.push({
      label: 'Master ID',
      textOrComponent: String(providerOptions.MasterID)
    });
  }

  // SWAPI - Mode
  if (providerOptions.Mode) {
    keyValueData.push({
      label: 'Mode',
      textOrComponent: String(providerOptions.Mode)
    });
  }

  // SWAPI - Agent Name
  if (providerOptions.AgentName) {
    keyValueData.push({
      label: 'Agent Name',
      textOrComponent: String(providerOptions.AgentName)
    });
  }

  // SWAPI V1 - Agent Password (masked)
  if (isSwapiV1Company({ dataProvider })) {
    keyValueData.push({
      label: 'Agent Password',
      textOrComponent: '**********'
    });
  }

  // SWAPI - Username
  if (providerOptions.Username) {
    keyValueData.push({
      label: 'User Name',
      textOrComponent: String(providerOptions.Username)
    });
  }

  // SWAPI V1 - User Password (masked)
  if (isSwapiV1Company({ dataProvider })) {
    keyValueData.push({
      label: 'User Password',
      textOrComponent: '**********'
    });
  }

  // SWAPI - Agent User
  if (providerOptions.agentUser) {
    keyValueData.push({
      label: 'Agent User',
      textOrComponent: String(providerOptions.agentUser)
    });
  }

  // SWAPI V2 - Agent Secret (masked)
  if (isSwapiV2Company({ dataProvider })) {
    keyValueData.push({
      label: 'Agent Secret',
      textOrComponent: '**********'
    });
  }

  return <KeyValueGrid keyValueArray={keyValueData} gridItemSize={gridItemSize} />;
};
